import axios from "axios";
import { BASE_URL } from "../components/Axios/base_uri";
import { store2 } from "../redux/store2";

export const getAnnouncementsBaseUrl = () => {
  return BASE_URL.replace(/\/$/, "");
};

export const getAuthToken = (passedUser = null) => {
  try {
    // 1. Check passed user object
    const checkTarget = (target) => {
      if (!target) return "";
      if (typeof target === "string" && (target.startsWith("ey") || target.length > 25)) {
        return target;
      }
      if (target.access_token) return target.access_token;
      if (target.token) return target.token;
      if (target.accessToken) return target.accessToken;
      if (target.jwt) return target.jwt;

      if (Array.isArray(target)) {
        for (const item of target) {
          const res = checkTarget(item);
          if (res) return res;
        }
      }

      if (target.user) {
        const userRes = checkTarget(target.user);
        if (userRes) return userRes;
      }

      return "";
    };

    if (passedUser) {
      const fromPassed = checkTarget(passedUser);
      if (fromPassed) return fromPassed;
    }

    // 2. Check active Redux store state in memory
    try {
      const state = store2.getState?.();
      if (state?.auth) {
        const fromRedux = checkTarget(state.auth);
        if (fromRedux) return fromRedux;
      }
    } catch (e) {}

    // 3. Direct localStorage keys
    const directKeys = [
      "token",
      "access_token",
      "accessToken",
      "jwt",
      "SediHishamAdmindata",
      "adminToken",
      "user",
    ];
    for (const key of directKeys) {
      const val = localStorage.getItem(key);
      if (val) {
        if (typeof val === "string" && (val.startsWith("ey") || val.length > 25)) {
          return val;
        }
        try {
          const parsed = JSON.parse(val);
          const fromParsed = checkTarget(parsed);
          if (fromParsed) return fromParsed;
        } catch (e) {}
      }
    }

    // 4. Persisted Redux in localStorage
    const persisted = localStorage.getItem("persist:root");
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted);
        if (parsed.auth) {
          const auth = typeof parsed.auth === "string" ? JSON.parse(parsed.auth) : parsed.auth;
          const fromAuth = checkTarget(auth);
          if (fromAuth) return fromAuth;
        }
      } catch (e) {}
    }
  } catch (error) {
    console.error("Error retrieving auth token:", error);
  }
  return "";
};

const getConfig = (userContext = null) => {
  const token = getAuthToken(userContext);
  const cleanToken = token ? String(token).replace(/^Bearer\s+/i, "").trim() : "";
  const headers = {
    "Content-Type": "application/json",
  };
  const params = {};

  if (cleanToken) {
    headers["Authorization"] = `Bearer ${cleanToken}`;
    headers["token"] = cleanToken;
    headers["x-access-token"] = cleanToken;
    params["token"] = cleanToken;
  }

  return {
    headers,
    params,
    timeout: 8000,
  };
};

export const AnnouncementsService = {
  // GET all announcements (Admin)
  getAll: async (userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.get(`${baseUrl}/announcements`, getConfig(userContext));
    return response.data;
  },

  // GET active announcements (Public/Website)
  getActive: async () => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.get(`${baseUrl}/announcements/active`, {
      timeout: 6000,
    });
    return response.data;
  },

  // GET single announcement
  getById: async (id, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.get(`${baseUrl}/announcements/${id}`, getConfig(userContext));
    return response.data;
  },

  // POST create announcement
  create: async ({ text_ar, text_en, isActive = true, order = 1 }, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.post(
      `${baseUrl}/announcements`,
      {
        text_ar,
        text_en,
        isActive,
        order: Number(order) || 1,
      },
      getConfig(userContext)
    );
    return response.data;
  },

  // PATCH update announcement
  update: async (id, data, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const payload = {};
    if (data.text_ar !== undefined) payload.text_ar = data.text_ar;
    if (data.text_en !== undefined) payload.text_en = data.text_en;
    if (data.isActive !== undefined) payload.isActive = Boolean(data.isActive);
    if (data.order !== undefined) payload.order = Number(data.order);

    const response = await axios.patch(
      `${baseUrl}/announcements/${id}`,
      payload,
      getConfig(userContext)
    );
    return response.data;
  },

  // PATCH update active status
  updateStatus: async (id, isActive, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.patch(
      `${baseUrl}/announcements/${id}/status`,
      { isActive: Boolean(isActive) },
      getConfig(userContext)
    );
    return response.data;
  },

  // PATCH toggle active status
  toggleStatus: async (id, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.patch(
      `${baseUrl}/announcements/${id}/toggle`,
      {},
      getConfig(userContext)
    );
    return response.data;
  },

  // DELETE announcement
  delete: async (id, userContext = null) => {
    const baseUrl = getAnnouncementsBaseUrl();
    const response = await axios.delete(`${baseUrl}/announcements/${id}`, getConfig(userContext));
    return response.data;
  },
};

export default AnnouncementsService;
